import type { ReactNode } from 'react'

import styles from './Chip.module.css'

interface ChipProps {
  readonly children: ReactNode
  /** A dot in this colour, for tags and enum options that carry one. */
  readonly colour?: string
  readonly selected?: boolean
  readonly disabled?: boolean
  /** Omit for a static label; supply for a filter or a selectable option. */
  readonly onClick?: () => void
  readonly pressed?: boolean
}

/**
 * A small label, optionally selectable.
 *
 * Renders a `<button>` when it does something and a `<span>` when it does not.
 * A clickable `<span>` is unreachable by keyboard, and a `<button>` that does
 * nothing is an empty promise to anyone tabbing through.
 */
export function Chip({
  children,
  colour,
  selected = false,
  disabled = false,
  onClick,
  pressed,
}: ChipProps) {
  const classes = [
    styles['chip'],
    onClick ? styles['interactive'] : '',
    selected ? styles['selected'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      {colour !== undefined && (
        <span
          className={styles['swatch']}
          style={{ background: colour }}
          aria-hidden="true"
        />
      )}
      {children}
    </>
  )

  if (!onClick) {
    return <span className={classes}>{content}</span>
  }

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={pressed ?? selected}
    >
      {content}
    </button>
  )
}
